import {z} from 'zod';
import type {SQL} from 'drizzle-orm';
import {asc, desc, count} from 'drizzle-orm';
import type {PgTable, PgColumn} from 'drizzle-orm/pg-core';
import type {NodePgDatabase} from 'drizzle-orm/node-postgres';

export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 50;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE).optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional()
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const paginationMetaSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  totalPages: z.number()
});

export function paginatedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    pagination: paginationMetaSchema
  });
}

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function parsePaginationParams(
  query: PaginationQuery,
  defaults: {sortBy?: string; sortOrder?: 'asc' | 'desc'} = {}
): PaginationParams {
  return {
    page: query.page ?? 1,
    pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
    sortBy: query.sortBy ?? defaults.sortBy,
    sortOrder: query.sortOrder ?? defaults.sortOrder ?? 'asc'
  };
}

export function getSortColumn(
  _table: PgTable,
  sortBy: string | undefined,
  allowedColumns: Record<string, PgColumn>
): PgColumn | undefined {
  if (!sortBy) return undefined;
  return allowedColumns[sortBy];
}

export function buildOrderBy(column: PgColumn | undefined, order: 'asc' | 'desc'): SQL | undefined {
  if (!column) return undefined;
  return order === 'desc' ? desc(column) : asc(column);
}

export async function countTotal(db: NodePgDatabase, table: PgTable, where?: SQL): Promise<number> {
  const conditions = where ? [where] : [];
  const result = await db
    .select({count: count()})
    .from(table)
    .where(conditions.length > 0 ? conditions[0] : undefined);
  return result[0].count;
}

export function buildPaginationMeta(total: number, params: PaginationParams) {
  return {
    page: params.page,
    pageSize: params.pageSize,
    total,
    totalPages: Math.ceil(total / params.pageSize)
  };
}
